import { Fragment, useState, useEffect } from "react";
import { toWords } from "number-to-words";
import { loadStripe } from "@stripe/stripe-js";
import api from '~/api/api';
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";


function BuyPlan() {
    const pricePerMonth = 19;
    const discountPercent = 2;

    const [planType, setPlanType] = useState("month");
    const [quantity, setQuantity] = useState(1);
    const [startDate, setStartDate] = useState(new Date());
    const [dueDate, setDueDate] = useState(new Date());
    const [subtotal, setSubtotal] = useState(pricePerMonth);
    const [grandTotal, setGrandTotal] = useState(pricePerMonth);
    const [grandTotalInWords, setGrandTotalInWords] = useState("");

    const formatDate = (date) =>
        date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });

    useEffect(() => {
        const totalMonths = planType === "annual" ? quantity * 12 : quantity;

        const newDueDate = new Date(startDate);
        newDueDate.setMonth(startDate.getMonth() + Number(totalMonths));
        setDueDate(newDueDate);

        const total = pricePerMonth * totalMonths;
        const discount = (total * discountPercent) / 100;
        const grand = parseFloat((total - discount).toFixed(2));

        setSubtotal(total);
        setGrandTotal(grand);

        const [dollarsPart, centsPart] = grand.toString().split(".");
        let words = `${toWords(Number(dollarsPart))} dollar${
            Number(dollarsPart) !== 1 ? "s" : ""
        }`;

        if (centsPart && Number(centsPart) > 0) {
            const centsNum = Number(centsPart.padEnd(2, "0")); // 24 -> 24, 2 -> 20
            words += ` and ${toWords(centsNum)} cent${
                centsNum !== 1 ? "s" : ""
            }`;
        }

        const formatted =
            words.charAt(0).toUpperCase() + words.slice(1) + " USD";
        setGrandTotalInWords(formatted);
    }, [quantity, planType, startDate]);

    const handleCheckout = async () => {
        const response = await api.post("/stripe-payments/create-checkout-session", { Amount: 100 });
        window.location.href = response.data.Url;
    }

    return (
        <Fragment>
            <div className="bg-body dark__bg-gray-1100 p-4 mb-4 rounded-2">
                <div className="row g-4">
                    <div className="col-12 col-lg-2">
                        <div className="row g-4 g-lg-2">
                            <div className="col-12 col-sm-6 col-lg-12">
                                <div className="form-check">
                                    <input
                                        id="annualRadio"
                                        className="form-check-input"
                                        type="radio"
                                        name="planType"
                                        checked={planType === "annual"}
                                        onChange={() => setPlanType("annual")}
                                    />
                                    <label htmlFor="annualRadio" className="form-check-label">
                                        Annual
                                    </label>
                                </div>
                            </div>
                            <div className="col-12 col-sm-6 col-lg-12">
                                <div className="form-check">
                                    <input
                                        id="monthRadio"
                                        className="form-check-input"
                                        type="radio"
                                        name="planType"
                                        checked={planType === "month"}
                                        onChange={() => setPlanType("month")}
                                    />
                                    <label htmlFor="monthRadio" className="form-check-label">
                                        Month
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-sm-6 col-lg-5">
                        <div className="row g-4 gy-lg-5">
                            <div className="col-12 col-lg-4">
                                <div className="col-auto">
                                    <input
                                        type="number"
                                        min={1}
                                        max={100}
                                        value={quantity}
                                        onChange={(e) => setQuantity(Number(e.target.value))}
                                        className="mb-2 form-control"
                                    />
                                </div>
                                <p className="fs-9 text-body-secondary fw-semibold mb-0 ms-3">
                                    ${pricePerMonth} / Month
                                </p>
                            </div>

                            <div className="col-12 col-lg-4">
                                <p className="fs-9 text-body-secondary fw-semibold mb-0">
                                    Start Date
                                </p>
                                <p className="fs-9 fw-semibold mb-0 text-success">
                                    {formatDate(startDate)}
                                </p>
                            </div>

                            <div className="col-12 col-lg-4">
                                <p className="fs-9 text-body-secondary fw-semibold mb-0">
                                    Due Date
                                </p>
                                <p className="fs-9 text-success fw-semibold mb-0">
                                    {formatDate(dueDate)}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-0">
                <div className="table-responsive scrollbar">
                    <table className="table fs-9 text-body mb-0">
                        <thead className="bg-body-secondary">
                        <tr>
                            <th></th>
                            <th>SL NO.</th>
                            <th>Products</th>
                            <th className="text-center">Unit</th>
                            <th className="text-center">Quantity</th>
                            <th className="text-center">Price</th>
                            <th className="text-end">Total</th>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td></td>
                            <td className="align-middle">1</td>
                            <td className="align-middle">
                                <p className="line-clamp-1 mb-0 fw-semibold">
                                    Personal (basic)
                                </p>
                            </td>
                            <td className="align-middle text-center">
                                {planType === "annual" ? "12 Months" : "1 Month"}
                            </td>
                            <td className="align-middle text-center fw-semibold">
                                {quantity}
                            </td>
                            <td className="align-middle text-center fw-semibold">
                                ${pricePerMonth}
                            </td>
                            <td className="align-middle text-end fw-semibold">
                                ${subtotal.toFixed(2)}
                            </td>
                            <td></td>
                        </tr>

                        <tr className="bg-body-secondary">
                            <td></td>
                            <td className="align-middle fw-semibold" colSpan="5">
                                Subtotal
                            </td>
                            <td className="align-middle text-end fw-bold">
                                ${subtotal.toFixed(2)}
                            </td>
                            <td></td>
                        </tr>

                        <tr>
                            <td></td>
                            <td colSpan="3"></td>
                            <td className="align-middle fw-bold">Discount/Voucher</td>
                            <td className="align-middle fw-bold">{discountPercent}(%)</td>
                            <td className="align-middle text-end fw-semibold text-danger">
                                -${((subtotal * discountPercent) / 100).toFixed(2)}
                            </td>
                            <td></td>
                        </tr>

                        <tr className="bg-body-secondary">
                            <td
                                className="align-middle ps-4 fw-bold text-body-highlight"
                                colSpan="3"
                            >
                                Grand Total
                            </td>
                            <td
                                className="align-middle fw-bold text-body-highlight"
                                colSpan="3"
                            >
                                {grandTotalInWords}
                            </td>
                            <td className="align-middle text-end fw-bold">
                                ${grandTotal.toFixed(2)}
                            </td>
                            <td></td>
                        </tr>
                        </tbody>
                    </table>
                </div>
                <div className="text-center mt-4">
                    <button className="btn btn-bt-primary"
                            onClick={handleCheckout}
                    >
                        Checkout
                    </button>
                </div>
            </div>
        </Fragment>
    );
}

export default BuyPlan;
